import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";

import { PinTagsSection } from "./pin-tags-section";
import { CustomButton } from "../../../atoms";
import { pxToRem } from "../../../../util";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { cloneDeep, debounce } from "lodash";
import {
  addSession,
  itemsFromBillString,
} from "../../../../redux/actions/cartAction";
import {
  IBill,
  validatePinTagsAction,
} from "../../../../redux/actions/pintagAction";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import { ICartSession } from "../../../../typings/interfaces/pos/cart-session";
import CustomModal from "../../custom-modal/custom-modal";

export interface IAssignPinTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceTypeId: number;
  itemsCount: number;
  sessionId: string;
  bill?: IBill;
  initialColor?: string;
  initialDate?: string;
  initialTags?: string[];
  onSubmit: (session: ICartSession) => void;
  setWasSessionAddedFromModal?: (val: boolean) => void;
  pinTagDraftSession?: {
    sessionId: string;
    customerId: number;
    cartItems: any[];
    pinTag: {
      color: string | null;
      tags: string[];
      date: string | null;
      isSpecial?: boolean;
    };
  };
  title?: string;
}

interface UpdatePintagDraft {
  billId: string;
  dueDate: string | null;
  pintagColor: string | null;
  pintagColorId?: number;
  pintagAlias?: string;
  day?: string;
  tags: string[];
}

export const AssignPinTagModal: FC<IAssignPinTagModalProps> = ({
  isOpen,
  onClose,
  serviceTypeId,
  itemsCount,
  sessionId,
  onSubmit,
  setWasSessionAddedFromModal,
  title,
  bill,
  initialColor: initialColorProp,
  initialDate: initialDateProp,
  initialTags: initialTagsProp,
}) => {
  const [isCompleted, setIsCompleted] = useState(false);
  const { cart } = useAppSelector((state) => state.cart);
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const [newSession, setNewSession] = useState<any>(null);
  const [resetTagsTrigger, setResetTagsTrigger] = useState(0);
  const [resetFlipIndex, setResetFlipIndex] = useState<number | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  // When editing, the bill's own tags must not count as already in use
  const { billId: routeBillId } = useParams();
  const excludeBillId = bill?.id ?? (routeBillId ? Number(routeBillId) : undefined);

  // Kept in a ref (instead of read directly from the closure) so the debounced
  // validator below stays a SINGLE stable function across re-renders instead
  // of being recreated - and its pending timer silently discarded - every
  // time `cart` gets a new array reference. `addSession` (dispatched
  // elsewhere, e.g. by another session's pin tag validation while this modal
  // is open) deep-clones the *entire* cart on every call, so `cart` churns
  // far more often than "this session's data actually changed". A `useMemo`
  // depending on `cart` directly used to fragment into several orphaned
  // debounce instances that kept toggling isValidating/isCompleted on their
  // own independent schedules - the Apply-button flicker. Mirrors the fix
  // already applied to PinTagDrawer.tsx's equivalent validator.
  const cartRef = useRef(cart);
  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  useEffect(() => {
    if (!isOpen) {
      setIsCompleted(false);
      setNewSession(null);
    }
  }, [isOpen]);

  const debouncedAssignPinTag = useMemo(
    () =>
      debounce(
        async (
          color: string | null,
          tags: string[],
          textBoxCount: number,
          colorId?: number,
          alias?: string,
          day?: string,
          date?: string | null,
          isSpecial?: boolean
        ) => {
          const targetTagList = tags.slice(0, textBoxCount);
          const isIncompleteTags =
            targetTagList.filter((tag) => tag.length !== 3).length > 0;

          setIsValidating(true);
          setIsCompleted(false);

          if (
            color === null ||
            tags.length === 0 ||
            targetTagList.includes("") ||
            isIncompleteTags
          ) {
            setIsValidating(false);
            return;
          }

          // PinTagsSection's seed effects re-fire this validator on open for
          // a session/bill that already has a pin tag assigned, carrying its
          // existing color/tags forward unchanged. The backend uniqueness
          // check has no way to know THIS bill already owns that tag - it
          // just sees the tag currently assigned to an in-store bill and
          // rejects it as taken, clearing the carried-forward tag out from
          // under the cashier. Skip the round-trip when the values being
          // submitted exactly match what's already saved; only a real change
          // needs to hit the backend. Mirrors the equivalent guard in
          // PinTagDrawer.tsx's debounced validator.
          const existingTags = bill
            ? bill.pintagTags
              ? String(bill.pintagTags)
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean)
              : []
            : cartRef.current.find((session) => session.sessionId === sessionId)
                ?.pinTag?.tags ?? [];
          const existingColor = bill
            ? bill.pintagColor || bill.pintagColorDescription || null
            : cartRef.current.find((session) => session.sessionId === sessionId)
                ?.pinTag?.color ?? null;
          const isUnchangedFromSaved =
            existingColor === color &&
            existingTags.length === targetTagList.length &&
            existingTags.every((t, i) => t === targetTagList[i]);

          if (isUnchangedFromSaved) {
            let newSessionClone;
            if (bill) {
              newSessionClone = {
                sessionId: sessionId,
                customerId: bill.customerId,
                cartItems: itemsFromBillString(bill.billString),
                pinTag: { color, tags: targetTagList, date: date || null, isSpecial },
              };
            } else {
              newSessionClone = cloneDeep(
                cartRef.current.find(
                  (session) => session.sessionId === sessionId
                )
              );
              if (newSessionClone) {
                newSessionClone.pinTag = {
                  color: color,
                  tags: targetTagList,
                  date: date || null,
                  isSpecial,
                };
              }
            }
            if (newSessionClone) {
              setNewSession(newSessionClone);
              setIsCompleted(true);
            }
            setIsValidating(false);
            return;
          }

          try {
            const dispatchResult = await dispatch(
              validatePinTagsAction({
                pintagColor: color,
                pintagColorId: colorId,
                pintagAlias: alias,
                tags: targetTagList,
                day: day,
                billId: excludeBillId,
              })
            ).unwrap();

            const isValid = dispatchResult?.response?.valid;
            const errorMessage =
              dispatchResult?.response?.errorMessage ||
              "Pin tag validation failed";

            if (!isValid) {
              setNewSession(null);
              setResetTagsTrigger((prev) => prev + 1);
              const unavailableTag =
                dispatchResult?.response?.unavailableTags?.[0];
              const invalidIndex = targetTagList.findIndex(
                (tag) => tag === unavailableTag
              );

              if (invalidIndex !== -1) {
                setResetFlipIndex(invalidIndex);
              }
              setIsCompleted(false);
              snackbar.showSnackbar(errorMessage, "error");
            } else {
              let newSessionClone;
              if (bill) {
                newSessionClone = {
                  sessionId: sessionId,
                  customerId: bill.customerId,
                  cartItems: itemsFromBillString(bill.billString),
                  pinTag: { color, tags: targetTagList, date: date || null, isSpecial },
                };
              } else {
                newSessionClone = cloneDeep(
                  cartRef.current.find(
                    (session) => session.sessionId === sessionId
                  )
                );
                if (newSessionClone) {
                  newSessionClone.pinTag = {
                    color: color,
                    tags: targetTagList,
                    date: date || null,
                    isSpecial,
                  };
                }
              }

              if (newSessionClone) {
                setNewSession(newSessionClone);
                setIsCompleted(true);
              }
            }
          } catch (err: any) {
            setIsCompleted(false);
            snackbar.showSnackbar("Validation failed", "error");
          } finally {
            setIsValidating(false);
          }
        },
        300
      ),
    // `cart` is intentionally NOT a dependency - see the cartRef comment
    // above; keeping this list to values that legitimately identify a
    // different validation target lets the debounce instance persist across
    // the `addSession` dispatch this same callback triggers.
    [dispatch, sessionId, snackbar, excludeBillId]
  );

  const handleTypingStart = () => {
    setIsCompleted(false);
    setIsValidating(true);
  };

  const handleApplySubmit = () => {
    if (
      newSession &&
      newSession.pinTag?.color &&
      newSession.pinTag?.tags?.length &&
      newSession.pinTag.tags.every(
        (tag: string) => tag.length === 3 && tag !== ""
      )
    ) {
      if (!bill) {
        dispatch(addSession(newSession));
        setWasSessionAddedFromModal?.(true);
      }
      onSubmit(newSession);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !isCalendarOpen && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
      // Only submit if form is valid and not currently validating
      if (isCompleted && !isValidating) {
        event.preventDefault();
        event.stopPropagation();
        handleApplySubmit();
      }
    }
  };

  const modalTitle = title ?? "Assign Pintag";

  // `bill` (pintag-table.tsx's "Update Pintag" screen) takes priority when
  // present; otherwise fall back to the caller-supplied initial* props
  // (cart.tsx threads the current session's own pinTag through these, the
  // same way checkinSummary.tsx already does for PinTagDrawer).
  const prefillTags = useMemo(
    () =>
      bill?.pintagTags
        ? String(bill.pintagTags)
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
        : initialTagsProp,
    [bill?.pintagTags, initialTagsProp]
  );

  const prefillColor =
    bill?.pintagColor || bill?.pintagColorDescription || initialColorProp || null;

  const prefillDate = bill?.dueDate
    ? new Date(bill.dueDate).toISOString()
    : initialDateProp;

  return (
    <CustomModal
      title={modalTitle}
      openModal={isOpen}
      onClose={onClose}
      width={pxToRem(727.87)}
      height={pxToRem(700.2)}
    >
      <Box
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: pxToRem(46.61),
          mt: { xs: pxToRem(-25), sm: pxToRem(33) },
          outline: "none",
        }}
      >
        <PinTagsSection
          serviceTypeId={serviceTypeId}
          itemsCount={itemsCount}
          sessionId={sessionId}
          isPintagModalSection={true}
          setIsCompleted={setIsCompleted}
          resetTagsTrigger={resetTagsTrigger}
          resetFlipIndex={resetFlipIndex}
          onChange={debouncedAssignPinTag}
          setResetFlipIndex={setResetFlipIndex}
          onTypingStart={handleTypingStart}
          onValidationIdle={() => setIsValidating(false)}
          onCalendarOpenChange={(isOpen) => setIsCalendarOpen(isOpen)}
          initialColor={prefillColor ?? undefined}
          initialDate={prefillDate}
          initialTags={prefillTags}
        />
        {!isCalendarOpen && (
          <CustomButton
            sx={{
              width: pxToRem(467),
              height: pxToRem(64),
              py: pxToRem(20),
              fontSize: pxToRem(24),
              fontWeight: "bold",
              borderRadius: pxToRem(10),
              alignSelf: "center",
              mt: { xs: pxToRem(-10), sm: pxToRem(0) },
            }}
            name="apply-pin-tag"
            onClick={handleApplySubmit}
            disabled={!isCompleted || isValidating}
          >
            Apply
          </CustomButton>
        )}
      </Box>
    </CustomModal>
  );
};
